import React, { useEffect } from 'react'
import { css } from '@emotion/core'
import { isTranslationAvailable } from '@fs/zion-locale'
import { Trans, useTranslation } from 'react-i18next'
import { Breadcrumbs, colors, H1, LayoutBand, Row, Separator, useOverlay, WhiteTheme } from '@fs/zion-ui'
import { useNavigate } from '../../../hooks'
import { getPageHeight } from '../../../lib/helpers'
import PreviewCard from '../common/PreviewCard'

import fnaSvg from '../../../images/Previews/ChooseYourDemo/fna.svg'
import bishopricOrBranchPresidentSvg from '../../../images/Previews/LandingPreview/GetStarted/leader.svg'
import wardcouncilOrPresidencyMemberSvg from '../../../images/Previews/LandingPreview/GetStarted/helper.svg'
import { setTryDemoLcrCalling } from '../../../lib/tryDemoServices'

// #region Styles
const cssBackgroundGradient = css`
  background-image: linear-gradient(${colors.blue70}, ${colors.blue05});
`
const cssUl = css`
  & > ul > li {
    margin-inline-start: 48px;
  }
`
// #endregion
const previewCardMinHeight = 51
const previewCardDescriptionMinHeight = 96

function ChooseYourDemo() {
  const [t] = useTranslation()
  const { isTryDemo, location, onReplace } = useNavigate()
  const minPageHeight = getPageHeight()
  const bishopricOrBranchPresidentOverlay = useOverlay()
  const wardCouncilOrPresidencyMemberOverlay = useOverlay()

  const breadcrumbs = [
    {
      text: t('app-name', 'Family Name Assist'),
      to: `/family-name-assist`,
      linkName: 'ChooseYourDemo:BreadCrumb to FNA:Click',
    },
    {
      text: t('breadcrumbs.choose-your-demo.tryDemo', 'Try Demo'),
    },
  ]

  useEffect(() => {
    if (!isTryDemo) {
      const hasQueryParamsInUrl = location.search.startsWith('?')
      const newSearch = `${location.search}${hasQueryParamsInUrl ? '&' : '?'}tryDemo=true`
      onReplace(location.pathname, undefined, true, false, newSearch)
    }
  }, [isTryDemo, location, onReplace])

  // #region Translations (excluding common-ui namespace strings)
  const pageHeading = t('tryDemo.choose-your-demo.heading', 'Choose your demo')
  const cards = [
    {
      heading: t('app-name', 'Family Name Assist'),
      headingMinHeight: previewCardMinHeight,
      description: t(
        'tryDemo.choose-your-demo.option.fna.description',
        'Practice using Family Name Assist to see how easily you can prepare and print a family name.'
      ),
      descriptionMinHeight: previewCardDescriptionMinHeight,
      linkName: 'ChooseYourDemo:Next (FNA):Click',
      linkUrl: '/add-name',
      rootTestId: 'fna-card',
      svgImage: fnaSvg,
    },
    {
      heading: t(
        'tryDemo.choose-your-demo.option.bishopric-or-branch-president.heading',
        'Bishopric or branch president'
      ),
      headingMinHeight: previewCardMinHeight,
      description: t(
        'tryDemo.choose-your-demo.option.bishopric-or-branch-president.description',
        'Practice finding and using Family Name Assist while issuing a temple recommend through Leader and Clerk Resources (LCR).'
      ),
      descriptionMinHeight: previewCardDescriptionMinHeight,
      linkName: 'ChooseYourDemo:Next (Bishopric or branch president):Click',
      linkUrl: '/lcr-demo/dashboard',
      onNext: () => setTryDemoLcrCalling('bishop'),
      overlay: bishopricOrBranchPresidentOverlay,
      overlayTitle: t(
        'tryDemo.choose-your-demo.bishopric-or-branch-president.overlay.title',
        'Which leaders have access to Family Name Assist while issuing a temple recommend within LCR?'
      ),
      overlayText: (
        <Trans
          i18nKey="tryDemo.choose-your-demo.bishopric-or-branch-president.overlay.text"
          components={{ ul: <ul css={cssUl} />, li: <li /> }}
          defaults="<ul><li>Bishops and their counselors</li><li>Branch presidents</li></ul>"
        />
      ),
      rootTestId: 'bishopric-or-branch-president-card',
      showOverlay:
        isTranslationAvailable('tryDemo.choose-your-demo.bishopric-or-branch-president.overlay.title') &&
        isTranslationAvailable('tryDemo.choose-your-demo.bishopric-or-branch-president.overlay.text'),
      svgImage: bishopricOrBranchPresidentSvg,
    },
    {
      heading: t(
        'tryDemo.choose-your-demo.option.ward-council-or-presidency-member.heading.v2',
        'Ward council or presidency member, or consultant'
      ),
      headingMinHeight: previewCardMinHeight,
      description: t(
        'tryDemo.choose-your-demo.option.ward-council-or-presidency-member.description',
        'Practice finding and using Family Name Assist from the Temple menu in Leader and Clerk Resources (LCR).'
      ),
      descriptionMinHeight: previewCardDescriptionMinHeight,
      linkName: 'ChooseYourDemo:Next (Ward council or presidency member):Click',
      linkUrl: '/lcr-demo/dashboard',
      onNext: () => setTryDemoLcrCalling('youngWomenPresident'),
      overlay: wardCouncilOrPresidencyMemberOverlay,
      overlayTitle: t(
        'tryDemo.choose-your-demo.ward-council-or-presidency-member.overlay.title',
        'Which leaders have access to Family Name Assist from LCR?'
      ),
      overlayText: (
        <Trans
          i18nKey="tryDemo.choose-your-demo.ward-council-or-presidency-member.overlay.text.v2"
          components={{ ul: <ul css={cssUl} />, li: <li /> }}
          defaults="<ul><li>Bishops and their counselors</li><li>Branch presidents and their counselors</li><li>Ward clerks</li><li>Executive secretaries</li><li>Ward council members and their presidencies</li><ul><li>Elders quorum presidents, counselors, and secretaries</li><li>Relief Society presidents, counselors, and secretaries</li><li>Young Women presidents, counselors, secretaries, advisers, and specialists</li><li>Young Men advisers and specialists</li><li>Primary presidents, counselors, and secretaries</li><li>Temple and family history leaders and consultants</li><li>Ward mission leaders</li><li>Sunday School presidents, counselors, and secretaries</li></ul></ul>"
        />
      ),
      rootTestId: 'ward-council-or-presidency-member-card',
      showOverlay:
        isTranslationAvailable('tryDemo.choose-your-demo.ward-council-or-presidency-member.overlay.title') &&
        isTranslationAvailable('tryDemo.choose-your-demo.ward-council-or-presidency-member.overlay.text.v2'),
      svgImage: wardcouncilOrPresidencyMemberSvg,
    },
  ]
  // #endregion

  return (
    <LayoutBand css={cssBackgroundGradient} minHeight={minPageHeight} maxWidth="xl">
      <Separator size="nano" />
      <WhiteTheme>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </WhiteTheme>
      <Separator size="xxl" />
      <WhiteTheme>
        <H1 centered size="H3">
          {pageHeading}
        </H1>
      </WhiteTheme>
      <Separator size="xl" />
      <Row alignX="center" gutters="xl" wrap>
        {cards.map((card) => (
          <PreviewCard key={card.heading} {...card} />
        ))}
      </Row>
      <Separator size="xl" />
    </LayoutBand>
  )
}

export default ChooseYourDemo
